import { format, isValid, parse } from "date-fns";
import { es } from "date-fns/esm/locale";
import { DateTimePickerMode } from "../interfaces";

/** Wire formats, identical to what the native inputs emitted. */
const VALUE_FORMATS: Record<DateTimePickerMode, string> = {
  datetime: "yyyy-MM-dd'T'HH:mm",
  date: "yyyy-MM-dd",
  time: "HH:mm",
};

/** What the closed field shows. Spanish month names, 12h clock. */
const DISPLAY_FORMATS: Record<DateTimePickerMode, string> = {
  datetime: "dd MMM yyyy, hh:mm a",
  date: "dd MMM yyyy",
  time: "hh:mm a",
};

/** The day a time-only picker anchors to, so `parse` has a full date to build. */
const TIME_ANCHOR: Date = new Date(2000, 0, 1);

/**
 * Reads the string a form holds into a Date.
 *
 * Values arrive from the API and from other forms, so anything unparseable is
 * reported as `null` rather than a partial date the user would have to correct.
 */
export const parsePickerValue = (
  value: string | number | undefined | null,
  mode: DateTimePickerMode,
): Date | null => {
  if (value === undefined || value === null) {
    return null;
  }

  const stringValue: string = typeof value === "string" ? value : String(value);
  if (!stringValue) {
    return null;
  }

  // `datetime` values may carry seconds, or a trailing zone, when they come
  // straight from the API; the leading portion is the part the picker owns.
  const trimmedValue: string =
    mode === "datetime" ? stringValue.slice(0, 16) : stringValue.slice(0, mode === "date" ? 10 : 5);

  const parsedDate: Date = parse(trimmedValue, VALUE_FORMATS[mode], TIME_ANCHOR);
  return isValid(parsedDate) ? parsedDate : null;
};

/** Writes a Date back into the string the form stores. */
export const formatPickerValue = (
  date: Date,
  mode: DateTimePickerMode,
): string => format(date, VALUE_FORMATS[mode]);

/** Builds the label shown in the closed field. */
export const formatDisplayValue = (
  date: Date,
  mode: DateTimePickerMode,
): string => format(date, DISPLAY_FORMATS[mode], { locale: es });

/** Placeholder shown while the field is empty, per mode. */
export const getDefaultPlaceholder = (mode: DateTimePickerMode): string => {
  if (mode === "date") {
    return "Selecciona una fecha";
  }
  if (mode === "time") {
    return "Selecciona una hora";
  }
  return "Selecciona fecha y hora";
};
